import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { FieldErrors, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CircularProgress } from "@mui/material";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { QuestionnaireStep } from "@/pages/Public/pages/Questionnaire/schema/questionnaire";

import { Icon } from "@/components";

import { ERROR_TYPE, translateError } from "@/utils";

import { useGetIndustriesQuery } from "@/pages/Private/redux/industry/industry.api";

import { useGetCompanySizesQuery } from "@/pages/Private/redux/company-size/company-size.api";

import { useGetCompanyRevenuesQuery } from "@/pages/Private/redux/company-revenue/company-revenue.api";
import { useGetCompanyBalancesQuery } from "@/pages/Private/redux/company-balance/company-balance.api";

import FormTextField from "../../../Account/components/FormTextField";
import FormLabel from "../../../ToDo/ManageToDos/components/EditableToDoItem/FormLabel";
import RichTextEditor from "../../../ToDo/ManageToDos/components/EditableToDoItem/RichTextEditor";
import StepOptionsFactory from "../StepOptionsFactory";
import IndustrySelectorFactory from "../IndustrySelectorFactory";
import CompanySizeSelectorFactory from "../CompanySizeSelectorFactory";
import CompanyRevenueSelectorFactory from "../CompanyRevenueSelectorFactory";
import CompanyBalanceSelectorFactory from "../CompanyBalanceSelectorFactory";

interface EditModeQuestionnaireItemProps {
	isLoading?: boolean;
	data?: QuestionnaireStep;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	validationSchema: any;
	setEditing: (value: number) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSubmit: (values: any) => void;
}

export default function EditModeQuestionnaireItem({
	data,
	validationSchema,
	setEditing,
	onSubmit,
	isLoading,
}: EditModeQuestionnaireItemProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`manage-questionnaire.${key}`);
	const [currentFormState, setCurrentFormState] = useState(data);

	const { data: industries = [] } = useGetIndustriesQuery();
	const filteredIndustries = (industries ?? []).filter(
		(industry): industry is { id: number; name: string } => typeof industry.id === "number"
	);

	const { data: companySizes = [] } = useGetCompanySizesQuery();
	const filteredCompanySizes = (companySizes ?? []).filter(
		(companySize): companySize is { id: number; value: string } =>
			typeof companySize.id === "number"
	);

	const { data: companyRevenues = [] } = useGetCompanyRevenuesQuery();
	const filteredCompanyRevenues = (companyRevenues ?? []).filter(
		(companyRevenue): companyRevenue is { id: number; value: string } =>
			typeof companyRevenue.id === "number"
	);

	const { data: companyBalances = [] } = useGetCompanyBalancesQuery();
	const filteredCompanyBalances = (companyBalances ?? []).filter(
		(companyBalance): companyBalance is { id: number; value: string } =>
			typeof companyBalance.id === "number"
	);

	const {
		control,
		register,
		formState: { errors },
		setValue,
		watch,
		handleSubmit,
	} = useForm({
		defaultValues: {
			...data,
			industryIds: data?.industryIds ?? [],
			companySizeIds: data?.companySizeIds ?? [],
			companyRevenueIds: data?.companyRevenueIds ?? [],
			companyBalanceIds: data?.companyBalanceIds ?? [],
		},
		resolver: zodResolver(validationSchema),
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "options",
	});

	const handleAddOption = useCallback(() => {
		append({ title: "" });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleRemoveOption = useCallback((index: number) => {
		remove(index);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof QuestionnaireStep) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	return (
		<form
			className="w-full rounded-xl p-4 bg-white"
			style={{ boxShadow: "0px 1px 2px 0px #11110F1F" }}
		>
			{isLoading && (
				<div className="h-96 flex items-center justify-center">
					<CircularProgress className="!w-14 !h-14" />
				</div>
			)}
			{!isLoading && (
				<>
					<div className="grid grid-cols-8 gap-y-5">
						<FormLabel className="mt-4 col-span-2" title={ts("form-fields.title")} />
						<FormTextField
							autoComplete="title"
							containerClassName="col-span-6 !mb-0"
							errorMessage={errors?.title?.message}
							formRegister={register("title")}
							label={ts("form-fields.title")}
							showLabel={false}
						/>
						<FormLabel className="mt-4 col-span-2" title={ts("form-fields.description")} />
						<RichTextEditor
							className="col-span-6"
							errorMessage={errors?.description?.message}
							placeholder={ts("add-details")}
							setValue={(value) => handleSaveFormState(value, "description")}
							value={watch("description")}
						/>
						<FormLabel className="mt-4 col-span-2" title={ts("select-industries")} />
						<IndustrySelectorFactory
							containerClassName="col-span-6 !mb-0"
							error={errors?.industryIds?.message}
							industries={filteredIndustries}
							setValue={setValue}
							watch={watch}
						/>
						<FormLabel className="mt-4 col-span-2" title={ts("select-company-sizes")} />
						<CompanySizeSelectorFactory
							companySizes={filteredCompanySizes}
							containerClassName="col-span-6 !mb-0"
							error={errors?.companySizeIds?.message}
							setValue={setValue}
							watch={watch}
						/>
						<FormLabel className="mt-4 col-span-2" title={ts("select-company-revenues")} />
						<CompanyRevenueSelectorFactory
							companyRevenues={filteredCompanyRevenues}
							containerClassName="col-span-6 !mb-0"
							error={errors?.companyRevenueIds?.message}
							setValue={setValue}
							watch={watch}
						/>
						<FormLabel className="mt-4 col-span-2" title={ts("select-company-balances")} />
						<CompanyBalanceSelectorFactory
							companyBalances={filteredCompanyBalances}
							containerClassName="col-span-6 !mb-0"
							error={errors?.companyBalanceIds?.message}
							setValue={setValue}
							watch={watch}
						/>
						<StepOptionsFactory
							className="col-span-8"
							errors={errors.options as FieldErrors | undefined}
							fields={fields}
							register={register}
							removeItem={handleRemoveOption}
							setValue={setValue}
							watch={watch}
						/>
						<div className="w-full relative col-start-3">
							<Button
								className="!text-[14px] !p-0 !font-medium leading-6 !w-fit !bg-transparent !text-primary-blue !shadow-none"
								image={<Icon className="text-primary-blue w-5 h-5 mr-2" icon={faPlus} />}
								title={ts("addAnswer")}
								type="button"
								onClick={handleAddOption}
							/>
							{!!errors?.options?.message && (
								<p className="absolute text-nowrap -bottom-5 text-[12px] text-danger w-full">
									{translateError[errors?.options?.message as unknown as ERROR_TYPE] ??
										errors?.options?.message}
								</p>
							)}
						</div>
					</div>

					<div className="flex items-center justify-end gap-2 mt-6">
						<Button
							className="!min-w-[110px] !max-w-[110px] !min-h-[44px] !max-h-[44px]"
							color={ButtonColor.ACTION_SECONDARY}
							title={t("basics.cancel")}
							type="button"
							onClick={() => setEditing(-1)}
						/>
						<Button
							className="!min-w-[110px] !max-w-[110px] !min-h-[44px] !max-h-[44px]"
							color={ButtonColor.ACTION}
							disabled={isLoading}
							isLoading={isLoading}
							title={data?.id ? t("basics.save") : t("basics.add")}
							type="button"
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</>
			)}
		</form>
	);
}
